import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { UserRole } from '@/types'
import { useAppDispatch } from '@store/index'
import { login } from '@store/slices/authSlice'
import PatientIntakeConsentDialog from './PatientIntakeConsentDialog'

interface PatientConsentGuardProps {
  children: React.ReactNode
}

/**
 * Guard component that ensures patients have signed the intake consent packet
 * before accessing protected routes
 */
const PatientConsentGuard: React.FC<PatientConsentGuardProps> = ({
  children,
}) => {
  const { user, token, refreshToken, role } = useAuth()
  const dispatch = useAppDispatch()
  const [showConsentDialog, setShowConsentDialog] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const checkConsentStatus = async () => {
      // Only check for patient/client role
      if (role !== UserRole.CLIENT || !user) {
        setIsChecking(false)
        return
      }

      // Skip consent check if user is logged in as a family member
      // Check for indicators that user is logged in as family member:
      // - originalPatient in localStorage (set when logging in as family member)
      // - relationship in localStorage (set when logging in as family member)
      const originalPatient = localStorage.getItem('originalPatient')
      const relationship = localStorage.getItem('relationship')

      // If user is logged in as a family member, skip consent check
      // originalPatient is the most reliable indicator as it's only set when switching accounts
      if (originalPatient || relationship) {
        setIsChecking(false)
        return
      }

      // Check if user has signed consent
      // If hasSignedConsent is explicitly false or undefined, show dialog
      if (
        user.hasSignedConsent === false ||
        user.hasSignedConsent === undefined
      ) {
        setShowConsentDialog(true)
      }

      setIsChecking(false)
    }

    checkConsentStatus()
  }, [user, role])

  const handleConsentSubmitted = async () => {
    if (!user || !token) return

    // Update user object with hasSignedConsent: true
    const updatedUser = {
      ...user,
      hasSignedConsent: true,
    }

    // Update Redux store
    dispatch(
      login({
        user: updatedUser,
        token,
        refreshToken: refreshToken || '',
      })
    )

    setShowConsentDialog(false)
  }

  // Show loading while checking consent status
  if (isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Verifying consent status...
        </Typography>
      </Box>
    )
  }

  // Show consent dialog if needed
  if (showConsentDialog && user) {
    return (
      <>
        <PatientIntakeConsentDialog
          open={showConsentDialog}
          user={user}
          onConsentSubmitted={handleConsentSubmitted}
        />
        {/* Block content while dialog is open */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300, // Below dialog but above content
            pointerEvents: 'none',
          }}
        />
      </>
    )
  }

  // Render children if consent is signed
  return <>{children}</>
}

export default PatientConsentGuard
